import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format, startOfMonth, subMonths } from 'date-fns'
import {
  ArrowLeft, Phone, Mail, MapPin, Award, BookOpen, Coins, Users, Wallet,
  Clock, FileText, Lock, Ban, Gift, CalendarRange, ChevronRight, CalendarPlus, BarChart3, Search,
} from 'lucide-react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { formatRate } from '../lib/rates'
import { formatMoney } from '../lib/pricing'
import {
  isDelivered, safeMinutes, computeOwed, groupByMonth, groupByStudent, MonthBucket,
} from '../lib/payout'

type TutorRow = {
  id: string; name: string; email: string; phone: string | null; address: string | null
  level: 'Regular' | 'Premium' | 'Expert' | null; subjects: string[] | null; hourly_rate: number
  active: boolean; notes: string | null; created_at?: string
}
type StudentLite = {
  id: string; name: string; grade: string | null; balance_minutes: number; status: 'active' | 'inactive'
}
type SessionRow = {
  id: string; student_id: string; started_at: string; ended_at: string; minutes: number
  notes: string | null; approval_status: 'pending' | 'approved' | 'rejected'
  session_type: 'tutoring' | 'consulting'; lifecycle_status: 'completed' | 'cancelled'
  is_free: boolean; cancel_reason: string | null
}

function minutesToHuman(min: number): string {
  const sign = min < 0 ? '-' : ''
  const m = Math.abs(min)
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h === 0) return `${sign}${r}m`
  return `${sign}${h}h ${r}m`
}

type RangePreset = 'all' | '30d' | 'month' | 'custom'
type StatusFilter = 'all' | 'approved' | 'pending' | 'rejected'

/** Fetch the tutor's full session history, paging past Supabase's 1000-row cap. */
async function fetchAllTutorSessions(tutorId: string): Promise<SessionRow[]> {
  const pageSize = 1000
  const rows: SessionRow[] = []
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('sessions')
      .select('id, student_id, started_at, ended_at, minutes, notes, approval_status, session_type, lifecycle_status, is_free, cancel_reason')
      .eq('tutor_id', tutorId)
      // id tiebreaker keeps page boundaries stable when started_at values collide.
      .order('started_at', { ascending: false })
      .order('id', { ascending: true })
      .range(from, from + pageSize - 1)
    if (error) throw error
    rows.push(...((data as SessionRow[]) || []))
    if (!data || data.length < pageSize) return rows
  }
}

export function TutorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [tutor, setTutor] = useState<TutorRow | null>(null)
  const [students, setStudents] = useState<StudentLite[]>([])
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [studentNames, setStudentNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [viewingNotes, setViewingNotes] = useState<SessionRow | null>(null)

  // Date filter
  const [preset, setPreset] = useState<RangePreset>('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sessionSearch, setSessionSearch] = useState('')

  // Money (admin-only; page is AdminRoute-wrapped and billing tables are RLS-locked).
  // Raw rate kept separately from the display coercion: null means "no rate set", not $0.
  const [tutorRate, setTutorRate] = useState<number | null>(null)
  const [overrides, setOverrides] = useState<Map<string, number | null>>(new Map())

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const { data: t, error: tErr } = await supabase.from('tutors').select('*').eq('id', id).single()
      if (tErr || !t) { setForbidden(true); setLoading(false); return }

      // Hourly rate is money — stored in the admin-only tutor_billing table.
      const { data: tb } = await supabase.from('tutor_billing').select('hourly_rate').eq('tutor_id', id).maybeSingle()
      setTutor({ ...(t as Record<string, unknown>), hourly_rate: tb?.hourly_rate ?? 0 } as TutorRow)
      setTutorRate(tb?.hourly_rate ?? null)

      const [{ data: studs }, sessRows] = await Promise.all([
        supabase.from('students').select('id, name, grade, balance_minutes, status').eq('tutor_id', id).order('name'),
        fetchAllTutorSessions(id),
      ])
      const studRows = (studs as StudentLite[]) || []
      setStudents(studRows)
      setSessions(sessRows)

      // Build a name map covering BOTH currently-assigned students and any
      // students referenced by past sessions (who may have been reassigned).
      const nameMap: Record<string, string> = {}
      studRows.forEach((s) => { nameMap[s.id] = s.name })
      const missing = Array.from(new Set(sessRows.map((s) => s.student_id))).filter((sid) => !nameMap[sid])
      if (missing.length) {
        const { data: named } = await supabase.from('students').select('id, name').in('id', missing)
        ;(named || []).forEach((s: { id: string; name: string }) => { nameMap[s.id] = s.name })
      }
      setStudentNames(nameMap)

      // Per-student rate overrides (money, admin-only) for every student this
      // tutor has sessions with — including students since reassigned.
      const allStudentIds = Array.from(new Set([...studRows.map((s) => s.id), ...sessRows.map((s) => s.student_id)]))
      if (allStudentIds.length) {
        const { data: sb } = await supabase
          .from('student_billing')
          .select('student_id, hourly_rate_override')
          .in('student_id', allStudentIds)
        setOverrides(new Map((sb || []).map((r) => [r.student_id, r.hourly_rate_override])))
      } else {
        setOverrides(new Map())
      }
    } catch (e) {
      console.error('Error loading tutor detail:', e)
      setForbidden(true)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  // Resolve the active [from, to) window from the preset / custom inputs.
  const range = useMemo(() => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0)
    if (preset === '30d') {
      const from = new Date(startOfToday); from.setDate(from.getDate() - 30)
      return { from, to: null as Date | null }
    }
    if (preset === 'month') {
      const from = new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1)
      return { from, to: null as Date | null }
    }
    if (preset === 'custom') {
      const from = fromDate ? new Date(fromDate + 'T00:00:00') : null
      const to = toDate ? new Date(toDate + 'T23:59:59') : null
      return { from, to }
    }
    return { from: null as Date | null, to: null as Date | null }
  }, [preset, fromDate, toDate])

  // Date layer — drives stats, owed and the breakdowns.
  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      const d = new Date(s.started_at)
      if (range.from && d < range.from) return false
      if (range.to && d > range.to) return false
      return true
    })
  }, [sessions, range])

  // Search + status layers — drive ONLY the session list (and the pill counts).
  // Money never moves with the search box or these pills.
  const searchedSessions = useMemo(() => {
    const q = sessionSearch.trim().toLowerCase()
    if (!q) return filteredSessions
    return filteredSessions.filter((s) => (studentNames[s.student_id] || '').toLowerCase().includes(q))
  }, [filteredSessions, sessionSearch, studentNames])

  const visibleSessions = useMemo(
    () => (statusFilter === 'all' ? searchedSessions : searchedSessions.filter((s) => s.approval_status === statusFilter)),
    [searchedSessions, statusFilter]
  )

  const statusCounts = useMemo(() => ({
    all: searchedSessions.length,
    approved: searchedSessions.filter((s) => s.approval_status === 'approved').length,
    pending: searchedSessions.filter((s) => s.approval_status === 'pending').length,
    rejected: searchedSessions.filter((s) => s.approval_status === 'rejected').length,
  }), [searchedSessions])

  const stats = useMemo(() => {
    const taught = filteredSessions
      .filter(isDelivered)
      .reduce((sum, s) => sum + safeMinutes(s.minutes), 0)
    const cancelled = filteredSessions.filter((s) => s.lifecycle_status === 'cancelled').length
    return { taught, cancelled, total: filteredSessions.length }
  }, [filteredSessions])

  const owed = useMemo(
    () => computeOwed(filteredSessions, overrides, tutorRate),
    [filteredSessions, overrides, tutorRate]
  )

  // Monthly buckets: last 12 months on "All time", otherwise the selected range.
  const monthWindow = useMemo(() => {
    const now = new Date()
    if (preset === 'all') return { from: startOfMonth(subMonths(now, 11)), to: startOfMonth(now) }
    return { from: range.from, to: range.to ?? now }
  }, [preset, range])

  // Feed the date-filtered list so partially-covered boundary months chart
  // only in-range sessions; the window still zero-fills the full month span.
  const monthly = useMemo(
    () => groupByMonth(filteredSessions.filter(isDelivered), monthWindow),
    [filteredSessions, monthWindow]
  )

  const byStudent = useMemo(
    () => groupByStudent(filteredSessions.filter(isDelivered)),
    [filteredSessions]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400" />
      </div>
    )
  }

  if (forbidden || !tutor) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 dark:bg-rose-900/20">
          <Lock className="h-7 w-7 text-rose-500" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tutor not available</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">This tutor doesn't exist or you don't have access.</p>
        <button onClick={() => navigate('/dashboard/tutors')}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          <ArrowLeft className="h-4 w-4" /> Back to tutors
        </button>
      </div>
    )
  }

  const activeStudents = students.filter((s) => s.status !== 'inactive').length

  // Owed card copy: never show a silently wrong $0 when rates are missing.
  const owedAllMissing = owed.payableCount > 0 && owed.missingRateCount === owed.payableCount
  const owedValue = owedAllMissing ? '—' : formatMoney(owed.total)
  const owedSub = owedAllMissing
    ? 'no rate set'
    : owed.missingRateCount > 0
      ? `${owed.missingRateCount} w/o rate`
      : 'in range'

  return (
    <div className="max-w-6xl mx-auto pb-10">
      {/* Back link */}
      <Link to="/dashboard/tutors"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 mb-4">
        <ArrowLeft className="h-4 w-4" /> Tutors
      </Link>

      {/* Identity header */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 overflow-hidden">
        <div className="p-6 sm:p-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 text-white text-2xl font-bold">
              {tutor.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{tutor.name}</h1>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  tutor.active !== false
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}>{tutor.active !== false ? 'active' : 'inactive'}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                {tutor.level && <span className="inline-flex items-center gap-1"><Award className="h-4 w-4" /> {tutor.level}</span>}
                <span className="inline-flex items-center gap-1"><Coins className="h-4 w-4" /> {formatRate(tutor.hourly_rate)}</span>
                {tutor.created_at && (
                  <span className="inline-flex items-center gap-1"><CalendarPlus className="h-4 w-4" /> Joined {format(new Date(tutor.created_at), 'MMM d, yyyy')}</span>
                )}
              </div>
              {tutor.subjects && tutor.subjects.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {tutor.subjects.map((subj, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-surface-0 text-xs text-gray-600 dark:text-gray-300">
                      <BookOpen className="h-3 w-3" /> {subj}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Key facts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
            <Fact icon={Users} label="Students" value={`${students.length}`} sub={`${activeStudents} active`} />
            <Fact icon={Clock} label="Taught" value={minutesToHuman(stats.taught)} sub="in range" />
            <Fact icon={CalendarRange} label="Sessions" value={`${stats.total}`} sub="in range" />
            <Fact icon={Wallet} label="Owed" value={owedValue} sub={owedSub} />
          </div>
        </div>

        {/* Contact strip */}
        <div className="border-t border-gray-100 dark:border-gray-800 px-6 sm:px-8 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
          <ContactItem icon={Mail} label="Email" value={tutor.email} />
          <ContactItem icon={Phone} label="Phone" value={tutor.phone} />
          {tutor.address && <ContactItem icon={MapPin} label="Address" value={tutor.address} />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Main column: sessions with date filter */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Session history" icon={Clock} hint="Filter the tutor's sessions by date, status, or student.">
            {/* Date + status filters, student search */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 dark:bg-surface-0 p-1 w-fit">
                  <FilterChip active={preset === 'all'} onClick={() => setPreset('all')} label="All time" />
                  <FilterChip active={preset === '30d'} onClick={() => setPreset('30d')} label="Last 30 days" />
                  <FilterChip active={preset === 'month'} onClick={() => setPreset('month')} label="This month" />
                  <FilterChip active={preset === 'custom'} onClick={() => setPreset('custom')} label="Custom" />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={sessionSearch}
                    onChange={(e) => setSessionSearch(e.target.value)}
                    placeholder="Search student..."
                    className="w-48 pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm placeholder:text-gray-400"
                  />
                  {sessionSearch && (
                    <button onClick={() => setSessionSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                      <span className="sr-only">Clear search</span>
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 dark:bg-surface-0 p-1 w-fit">
                <FilterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="All" count={statusCounts.all} />
                <FilterChip active={statusFilter === 'approved'} onClick={() => setStatusFilter('approved')} label="Approved" count={statusCounts.approved} />
                <FilterChip active={statusFilter === 'pending'} onClick={() => setStatusFilter('pending')} label="Pending" count={statusCounts.pending} />
                <FilterChip active={statusFilter === 'rejected'} onClick={() => setStatusFilter('rejected')} label="Rejected" count={statusCounts.rejected} />
              </div>
              {preset === 'custom' && (
                <div className="flex flex-wrap items-end gap-3">
                  <label className="block">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</span>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm" />
                  </label>
                  <label className="block">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</span>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm" />
                  </label>
                  {(fromDate || toDate) && (
                    <button onClick={() => { setFromDate(''); setToDate('') }}
                      className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>

            {visibleSessions.length === 0 ? (
              <EmptyHint icon={Clock}
                text={sessionSearch.trim()
                  ? `No ${statusFilter === 'all' ? '' : statusFilter + ' '}sessions matching “${sessionSearch.trim()}”.`
                  : `No ${statusFilter === 'all' ? '' : statusFilter + ' '}sessions in this period.`}
                sub={sessionSearch.trim()
                  ? 'Try a different name, date range, or status.'
                  : statusFilter === 'all' ? 'Try a wider date range.' : 'Try a wider date range or another status.'} />
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {visibleSessions.map((s) => (
                  <li key={s.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/dashboard/students/${s.student_id}`}
                          className="text-sm font-medium text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400">
                          {studentNames[s.student_id] || 'Unknown student'}
                        </Link>
                        {s.session_type === 'consulting' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            <Gift className="h-3 w-3" /> Free consulting
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {format(new Date(s.started_at), 'MMM d, yyyy')} · {format(new Date(s.started_at), 'h:mm a')}–{format(new Date(s.ended_at), 'h:mm a')} · {s.minutes} min
                        {s.lifecycle_status === 'cancelled' && s.cancel_reason ? ` · ${s.cancel_reason}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {s.notes && (
                        <button onClick={() => setViewingNotes(s)}
                          className="inline-flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline">
                          <FileText className="h-3.5 w-3.5" /> Notes
                        </button>
                      )}
                      <SessionStatusBadge session={s} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Monthly breakdown" icon={BarChart3}
            hint={preset === 'all' ? 'Approved hours per month · last 12 months' : 'Approved hours per month · in selected range'}>
            {monthly.length === 0 || monthly.every((m) => m.sessions === 0) ? (
              <EmptyHint icon={BarChart3} text="No approved sessions to chart." sub="Try a wider date range." />
            ) : (
              <MonthlyChart data={monthly} />
            )}
          </Section>
        </div>

        {/* Side column: students */}
        <div className="space-y-6">
          <Section title="Students" icon={Users} hint={`${students.length} assigned to ${tutor.name.split(' ')[0]}`}>
            {students.length === 0 ? (
              <EmptyHint icon={Users} text="No students assigned." />
            ) : (
              <ul className="space-y-2">
                {students.map((s) => (
                  <li key={s.id}>
                    <Link to={`/dashboard/students/${s.id}`}
                      className="group flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 hover:border-brand-300 dark:hover:border-brand-700 hover:bg-gray-50 dark:hover:bg-surface-0 transition-colors">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>
                        <p className="text-xs text-gray-400 flex items-center gap-1.5">
                          {s.grade ? `Grade ${s.grade}` : 'No grade'}
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span className={`inline-flex items-center gap-1 ${s.balance_minutes <= 0 ? 'text-rose-500' : ''}`}>
                            <Wallet className="h-3 w-3" /> {minutesToHuman(s.balance_minutes)}
                          </span>
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="By student" icon={Users} hint="Approved sessions · in selected range">
            {byStudent.length === 0 ? (
              <EmptyHint icon={Users} text="No approved sessions in this period." />
            ) : (
              <ul className="space-y-3">
                {byStudent.map((b) => (
                  <li key={b.studentId}>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <Link to={`/dashboard/students/${b.studentId}`}
                        className="font-medium text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 truncate">
                        {studentNames[b.studentId] || 'Unknown student'}
                      </Link>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {b.sessions} {b.sessions === 1 ? 'session' : 'sessions'} · {minutesToHuman(b.minutes)}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-surface-0 overflow-hidden">
                      <div className="h-full rounded-full bg-brand-400"
                        style={{ width: `${Math.max(2, Math.round(b.share * 100))}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>

      {/* Notes modal */}
      {viewingNotes && (
        <Modal onClose={() => setViewingNotes(null)} title="Session notes"
          subtitle={`${studentNames[viewingNotes.student_id] || 'Student'} • ${format(new Date(viewingNotes.started_at), 'MMM d, yyyy')}`}>
          <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {viewingNotes.notes}
          </pre>
        </Modal>
      )}
    </div>
  )
}

// ---- Small presentational helpers -------------------------------------

function Fact({ icon: Icon, label, value, sub }: {
  icon: typeof Wallet; label: string; value: string; sub?: string
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-surface-0 px-3 py-2.5 min-w-[6.5rem]">
      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-1 text-lg font-bold leading-none text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="mt-1 text-[11px] text-gray-400">{sub}</p>}
    </div>
  )
}

function ContactItem({ icon: Icon, label, value }: { icon: typeof Phone; label: string; value: string | null }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <span className="text-gray-400 dark:text-gray-500">{label}:</span>
      <span className="text-gray-700 dark:text-gray-200 truncate">{value || '—'}</span>
    </div>
  )
}

function Section({ title, icon: Icon, hint, children }: {
  title: string; icon: typeof Coins; hint?: string; children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
          <Icon className="h-4 w-4 text-brand-500" /> {title}
        </h2>
        {hint && <p className="text-xs text-gray-400 mt-0.5 ml-6">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

function FilterChip({ active, onClick, label, count }: {
  active: boolean; onClick: () => void; label: string; count?: number
}) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
      {label}
      {count !== undefined && (
        <span className={`ml-1.5 text-xs ${active ? 'text-brand-500' : 'text-gray-400 dark:text-gray-500'}`}>{count}</span>
      )}
    </button>
  )
}

function EmptyHint({ icon: Icon, text, sub }: { icon: typeof Clock; text: string; sub?: string }) {
  return (
    <div className="py-8 text-center">
      <Icon className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>
      {sub && <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">{sub}</p>}
    </div>
  )
}

function SessionStatusBadge({ session }: { session: SessionRow }) {
  const cls = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium'
  if (session.lifecycle_status === 'cancelled')
    return <span className={`${cls} bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}><Ban className="h-3 w-3" /> Cancelled</span>
  if (session.approval_status === 'approved')
    return <span className={`${cls} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300`}>Approved</span>
  if (session.approval_status === 'rejected')
    return <span className={`${cls} bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300`}>Rejected</span>
  return <span className={`${cls} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>Pending</span>
}

// Bar color is Tailwind brand-400; ticks/grid inherit the wrapper's text color
// via currentColor so the chart adapts to light/dark without theme detection.
function MonthlyChart({ data }: { data: MonthBucket[] }) {
  return (
    <div className="h-56 text-gray-400 dark:text-gray-500">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.2} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: 'currentColor', fontSize: 11 }}
            axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} axisLine={false} tickLine={false}
            width={40} allowDecimals={false} unit="h" />
          <Tooltip cursor={{ fill: 'currentColor', fillOpacity: 0.06 }} content={<MonthTooltip />} />
          <Bar dataKey="hours" fill="#3a6fd9" radius={[4, 4, 0, 0]} maxBarSize={32} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function MonthTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: MonthBucket }> }) {
  if (!active || !payload?.length) return null
  const b = payload[0].payload
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-gray-900 dark:text-white">{b.label}</p>
      <p className="text-gray-500 dark:text-gray-400 mt-0.5">
        {b.sessions} {b.sessions === 1 ? 'session' : 'sessions'} · {minutesToHuman(b.minutes)}
      </p>
    </div>
  )
}

function Modal({ title, subtitle, onClose, children }: {
  title: string; subtitle?: string; onClose: () => void; children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white dark:bg-surface-100 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-modalIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0 rounded-lg">
            <span className="sr-only">Close</span>
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>
  )
}
